/* =============================================================================
 * Seasonal Flavor Upsell — bundle builder checkout interstitial
 *
 * When the customer clicks "Check Out Now", if there are in-stock seasonal
 * flavors in the Bundle collection that are NOT already in their box, show a
 * popup offering to add one or more before proceeding. Declining (or an empty
 * selection) continues straight to the normal checkout flow.
 *
 * Integration points:
 *   - wiq-bundle-builder.js calls window.SeasonalUpsell.shouldIntercept(builder)
 *     and window.SeasonalUpsell.open(builder) from handleCheckoutModalOpener().
 *   - Adding a flavor reuses the builder's normal add path: set the card's
 *     <wiq-quantity-input> value and dispatch "change".
 *   - On accept, builder._seasonalUpsellAccepted / _seasonalUpsellSummary are
 *     read by the builder's /cart/update.js calls to write cart attributes,
 *     which a Shopify Flow turns into the "Upsell" order tag.
 * ========================================================================== */
(function () {
  'use strict';

  var MODAL_SELECTOR = '[data-seasonal-upsell-modal]';
  var MAX_PER_FLAVOR = 6;

  var SeasonalUpsell = {
    _accepted: false,
    _busy: false,
    _bound: false,
    _builder: null,
    _qty: null,

    modal: function () {
      return document.querySelector(MODAL_SELECTOR);
    },

    maxFlavors: function () {
      var modal = this.modal();
      var n = modal ? parseInt(modal.getAttribute('data-max'), 10) : 4;
      return (isFinite(n) && n > 0) ? n : 4;
    },

    /* Seasonal, in-stock (OOS cards are never rendered), non-gift-card, and not
     * already in the box. Featured flavors first, then the rest in DOM order
     * (which is popularity-sorted), capped to the configured maximum. */
    getEligibleCards: function (builder) {
      var selected = {};
      (builder && builder.selectedItems || []).forEach(function (item) {
        selected[String(item.productId)] = true;
      });

      var eligible = Array.prototype.filter.call(
        document.querySelectorAll('wiq-bundle-item'),
        function (card) {
          return card.dataset.seasonal === 'true' &&
            !card.hasAttribute('data-gift-card') &&
            !selected[String(card.dataset.productId)];
        }
      );

      var featured = eligible.filter(function (c) { return c.hasAttribute('data-upsell-featured'); });
      var rest = eligible.filter(function (c) { return !c.hasAttribute('data-upsell-featured'); });
      return featured.concat(rest).slice(0, this.maxFlavors());
    },

    shouldIntercept: function (builder) {
      if (this._accepted) return false;
      if (!this.modal()) return false;
      return this.getEligibleCards(builder).length > 0;
    },

    formatPrice: function (cents) {
      var value = parseInt(cents, 10) || 0;
      try {
        if (window.Shopify && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(value);
        }
      } catch (e) {}
      return '$' + (value / 100).toFixed(2);
    },

    escapeHtml: function (str) {
      return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    },

    // Subscribe & Save is a flat percentage off the one-time price; mirror it in
    // the popup so the price shown matches the option the customer has selected.
    subscriptionActive: function (builder) {
      return !!(builder && builder.purchaseOptions &&
                builder.purchaseOptions.selectedOption === 'subscription');
    },

    priceHtml: function (cents, builder) {
      var value = parseInt(cents, 10) || 0;
      if (this.subscriptionActive(builder)) {
        var pct = (builder && typeof builder.subscriptionPercentage === 'number' &&
                   builder.subscriptionPercentage > 0 && builder.subscriptionPercentage < 1)
          ? builder.subscriptionPercentage : 0.10; // Subscribe & Save is 10% as of 2026-10-13
        var discounted = Math.round(value * (1 - pct));
        return this.formatPrice(discounted) + '<span class="su-price-note">subscription</span>';
      }
      return this.formatPrice(value);
    },

    open: function (builder) {
      var modal = this.modal();
      if (!modal) { builder.runCheckout(); return; }

      var cards = this.getEligibleCards(builder);
      if (!cards.length) { builder.runCheckout(); return; }

      this._builder = builder;
      this._qty = {};
      this._busy = false;

      var grid = modal.querySelector('[data-seasonal-upsell-grid]');
      grid.innerHTML = cards.map(function (card) {
        var id = card.dataset.productId;
        var title = SeasonalUpsell.escapeHtml(card.dataset.productTitle ||
          (card.querySelector('.product-card__title') || {}).textContent || 'Seasonal flavor');
        var type = SeasonalUpsell.escapeHtml(card.dataset.productType ||
          (card.querySelector('.product-card__collections') || {}).textContent || '');
        var img = card.dataset.productImage ||
          (card.querySelector('.product-card__image') || {}).src || '';
        var price = SeasonalUpsell.priceHtml(card.dataset.price, builder);
        return (
          '<div class="seasonal-upsell-card" data-product-id="' + id + '">' +
            '<div class="seasonal-upsell-card__media">' +
              (img ? '<img src="' + img + '" alt="" loading="lazy">' : '') +
            '</div>' +
            '<div class="seasonal-upsell-card__name">' + title + '</div>' +
            (type ? '<div class="seasonal-upsell-card__type">' + type + '</div>' : '') +
            '<div class="seasonal-upsell-card__price">' + price + '</div>' +
            '<div class="seasonal-upsell-card__stepper">' +
              '<button type="button" data-su-step="-1" aria-label="Decrease">&minus;</button>' +
              '<span data-su-qty>0</span>' +
              '<button type="button" data-su-step="1" aria-label="Increase">+</button>' +
            '</div>' +
          '</div>'
        );
      }).join('');

      this._bindModal();
      this._syncAddButton();

      modal.setAttribute('data-active', '');
      document.body.classList.add('overflow-hidden');
      this._track('Seasonal Upsell Shown', { flavorCount: cards.length });
    },

    _bindModal: function () {
      if (this._bound) return;
      this._bound = true;
      var modal = this.modal();
      var self = this;

      modal.querySelectorAll('[data-seasonal-upsell-cancel]').forEach(function (el) {
        el.addEventListener('click', function () { self._close(); });
      });
      modal.querySelector('[data-seasonal-upsell-skip]').addEventListener('click', function () {
        self._resolve(false);
      });
      modal.querySelector('[data-seasonal-upsell-add]').addEventListener('click', function () {
        self._resolve(true);
      });

      modal.querySelector('[data-seasonal-upsell-grid]').addEventListener('click', function (event) {
        var btn = event.target.closest('[data-su-step]');
        if (!btn) return;
        var cardEl = btn.closest('.seasonal-upsell-card');
        var id = cardEl.getAttribute('data-product-id');
        var step = parseInt(btn.getAttribute('data-su-step'), 10);
        var next = Math.max(0, Math.min(MAX_PER_FLAVOR, (self._qty[id] || 0) + step));
        self._qty[id] = next;
        cardEl.querySelector('[data-su-qty]').textContent = String(next);
        cardEl.classList.toggle('is-selected', next > 0);
        self._syncAddButton();
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && modal.hasAttribute('data-active')) self._close();
      });
    },

    _selectedEntries: function () {
      var qty = this._qty || {};
      return Object.keys(qty)
        .filter(function (id) { return qty[id] > 0; })
        .map(function (id) { return [id, qty[id]]; });
    },

    _syncAddButton: function () {
      var addBtn = this.modal().querySelector('[data-seasonal-upsell-add]');
      var total = this._selectedEntries().reduce(function (sum, e) { return sum + e[1]; }, 0);
      addBtn.textContent = total > 0 ? ('Add ' + total + ' & Check Out') : 'Add & Check Out';
      addBtn.disabled = total === 0;
    },

    _resolve: function (accept) {
      if (this._busy) return;
      var builder = this._builder;
      var chosen = this._selectedEntries();

      if (!accept || !chosen.length) {
        this._track('Seasonal Upsell Skipped', {});
        this._close();
        builder.runCheckout();
        return;
      }

      this._busy = true;
      this._setButtonsDisabled(true);

      var summary = [];
      chosen.forEach(function (entry) {
        var card = document.querySelector('wiq-bundle-item[data-product-id="' + entry[0] + '"]');
        if (!card) return;
        var input = card.querySelector('wiq-quantity-input input');
        if (!input) return;
        var current = parseInt(input.value, 10) || 0;
        input.value = String(current + entry[1]);
        input.dispatchEvent(new Event('change', { bubbles: true }));
        summary.push((card.dataset.productHandle || entry[0]) + ':' + entry[1]);
      });

      builder._seasonalUpsellAccepted = true;
      builder._seasonalUpsellSummary = summary.join(',');
      this._accepted = true;
      this._track('Seasonal Upsell Accepted', { items: summary.join(',') });

      this._close();
      builder.runCheckout();
    },

    _setButtonsDisabled: function (disabled) {
      this.modal().querySelectorAll('button').forEach(function (b) { b.disabled = disabled; });
    },

    _close: function () {
      var modal = this.modal();
      if (modal) modal.removeAttribute('data-active');
      document.body.classList.remove('overflow-hidden');
    },

    _track: function (event, extra) {
      try {
        window.dataLayer = window.dataLayer || [];
        var payload = { event: event };
        if (extra) Object.keys(extra).forEach(function (k) { payload[k] = extra[k]; });
        window.dataLayer.push(payload);
      } catch (e) {}
    }
  };

  window.SeasonalUpsell = SeasonalUpsell;
})();
